import { Link, useLocation, useNavigate } from "react-router-dom";
import { ShoppingBag, Menu as MenuIcon, X, User, LogOut } from "lucide-react";
import { useState, useRef, useEffect } from "react";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { GoogleRatingBadge } from "@/components/site/GoogleRatingBadge";

const navLinks = [
  { to: "/menu", label: "Menu" },
  { to: "/about", label: "Story" },
  { to: "/faq", label: "FAQ" },
  { to: "/track", label: "Track order" },
];

export default function Nav() {
  const { count, setDrawerOpen } = useCart();
  const { user, loading: authLoading, logout } = useAuth();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [accountOpen, setAccountOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const accountRef = useRef(null);

  // Close the account dropdown on outside click.
  useEffect(() => {
    const handler = (e) => {
      if (accountRef.current && !accountRef.current.contains(e.target)) {
        setAccountOpen(false);
      }
    };
    if (accountOpen) document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [accountOpen]);

  const doLogout = async () => {
    setAccountOpen(false);
    setMobileOpen(false);
    await logout();
    navigate("/", { replace: true });
  };

  // Desktop pill — either "Sign in" for guests or the customer's first
  // name/email with a dropdown that reveals "My orders" + "Log out". While
  // auth is still resolving we reserve the width so the header doesn't
  // jump once /auth/me returns.
  const AccountControl = () => {
    if (authLoading) {
      return <span data-testid="nav-account-loading" className="uppercase tracking-[0.2em] text-xs lg:text-sm font-bold text-espresso-light/50 min-w-[80px] hidden md:inline-block">·</span>;
    }
    if (!user) {
      return (
        <Link
          to={`/login?next=${encodeURIComponent(location.pathname + location.search)}`}
          data-testid="nav-signin-link"
          className="hidden md:inline-flex uppercase tracking-[0.2em] text-xs lg:text-sm font-bold text-espresso-light hover:text-espresso link-underline items-center gap-2"
        >
          <User size={16} strokeWidth={1.5} />
          Sign in
        </Link>
      );
    }
    const first = (user.name || user.email || "").split(" ")[0].split("@")[0];
    return (
      <div className="hidden md:block relative" ref={accountRef}>
        <button
          onClick={() => setAccountOpen((v) => !v)}
          data-testid="nav-account-btn"
          className="uppercase tracking-[0.2em] text-xs lg:text-sm font-bold text-espresso-light hover:text-espresso flex items-center gap-2"
        >
          <User size={16} strokeWidth={1.5} />
          {first || "Account"}
        </button>
        {accountOpen && (
          <div
            data-testid="nav-account-menu"
            className="absolute right-0 mt-3 w-56 bg-ivory border border-line shadow-sm z-50"
          >
            <div className="px-4 py-3 border-b border-line">
              <div className="label-xs text-espresso-light">Signed in</div>
              <div className="mt-1 text-xs text-espresso truncate">{user.email}</div>
            </div>
            <Link
              to="/my-orders"
              onClick={() => setAccountOpen(false)}
              data-testid="nav-my-orders"
              className="block px-4 py-3 text-xs uppercase tracking-[0.2em] text-espresso hover:bg-ivory-100"
            >
              My orders
            </Link>
            <Link
              to="/account"
              onClick={() => setAccountOpen(false)}
              data-testid="nav-account-link"
              className="block px-4 py-3 text-xs uppercase tracking-[0.2em] text-espresso hover:bg-ivory-100 border-t border-line"
            >
              My account
            </Link>
            {user.is_admin && (
              <Link
                to="/admin/dashboard"
                onClick={() => setAccountOpen(false)}
                className="block px-4 py-3 text-xs uppercase tracking-[0.2em] text-gold-hover hover:bg-ivory-100 border-t border-line"
              >
                Admin dashboard
              </Link>
            )}
            <button
              onClick={doLogout}
              data-testid="nav-logout"
              className="w-full text-left px-4 py-3 text-xs uppercase tracking-[0.2em] text-espresso hover:bg-ivory-100 border-t border-line flex items-center gap-2"
            >
              <LogOut size={14} strokeWidth={1.5} />
              Log out
            </button>
          </div>
        )}
      </div>
    );
  };

  return (
    <>
      <header
        data-testid="site-nav"
        style={{ top: "var(--store-closed-banner-height, 0px)" }}
        className="fixed left-0 right-0 z-40 border-b border-line bg-[#FDFBF7]/85 backdrop-blur-xl"
      >
        <div className="mx-auto flex h-16 max-w-[1400px] items-center justify-between px-4 md:h-24 md:px-10">
          <Link to="/" data-testid="nav-logo" className="font-serif text-2xl tracking-[0.06em] text-espresso font-semibold md:text-3xl lg:text-4xl">
            TIRAMISÙ <span className="text-gold">&amp;</span> Co.
          </Link>

          <nav className="hidden md:flex items-center gap-10 lg:gap-12">
            {navLinks.map((l) => (
              <Link
                key={l.to}
                to={l.to}
                data-testid={`nav-link-${l.label.toLowerCase().replace(" ", "-")}`}
                className={`link-underline uppercase tracking-[0.2em] text-xs lg:text-sm font-bold ${
                  location.pathname === l.to ? "text-espresso" : "text-espresso-light"
                }`}
              >
                {l.label}
              </Link>
            ))}
          </nav>

          <div className="flex items-center gap-3 md:gap-5">
            <div className="hidden sm:block">
              <GoogleRatingBadge variant="nav" />
            </div>
            <AccountControl />
            <button
              onClick={() => setDrawerOpen(true)}
              data-testid="nav-cart-btn"
              className="tap-target relative flex items-center gap-2.5 text-espresso transition-colors duration-200 hover:text-gold-hover"
            >
              <ShoppingBag size={22} strokeWidth={1.5} />
              <span className="uppercase tracking-[0.2em] text-xs lg:text-sm font-bold hidden sm:inline">Cart</span>
              {count > 0 && (
                <span
                  data-testid="nav-cart-count"
                  className="absolute -top-2 -right-2 bg-espresso text-ivory text-[10px] w-5 h-5 rounded-full flex items-center justify-center font-medium"
                >
                  {count}
                </span>
              )}
            </button>
            <button
              onClick={() => setMobileOpen(true)}
              data-testid="nav-mobile-toggle"
              aria-label="Open menu"
              className="tap-target text-espresso md:hidden"
            >
              <MenuIcon size={26} strokeWidth={1.5} />
            </button>
          </div>
        </div>
      </header>

      {mobileOpen && (
        <div className="fixed inset-0 z-50 bg-ivory md:hidden" data-testid="mobile-menu">
          <div className="flex items-center justify-between p-6 border-b border-line">
            <span className="font-serif text-2xl md:text-3xl tracking-[0.06em] text-espresso font-semibold">
              TIRAMISÙ <span className="text-gold">&amp;</span> Co.
            </span>
            <button onClick={() => setMobileOpen(false)} data-testid="mobile-menu-close">
              <X size={24} strokeWidth={1.5} />
            </button>
          </div>
          <div className="p-8 flex flex-col gap-6">
            {navLinks.map((l) => (
              <Link
                key={l.to}
                to={l.to}
                onClick={() => setMobileOpen(false)}
                className="font-serif text-3xl text-espresso"
              >
                {l.label}
              </Link>
            ))}
            <div className="mt-4 border-t border-line pt-6">
              {authLoading ? null : !user ? (
                <Link
                  to={`/login?next=${encodeURIComponent(location.pathname + location.search)}`}
                  data-testid="mobile-signin-link"
                  onClick={() => setMobileOpen(false)}
                  className="font-serif text-3xl text-espresso flex items-center gap-3"
                >
                  <User size={22} strokeWidth={1.5} /> Sign in
                </Link>
              ) : (
                <>
                  <div className="label-xs text-espresso-light">Signed in as</div>
                  <div className="mt-1 text-sm text-espresso truncate">{user.email}</div>
                  <Link
                    to="/my-orders"
                    onClick={() => setMobileOpen(false)}
                    className="mt-4 font-serif text-2xl text-espresso block"
                    data-testid="mobile-my-orders"
                  >
                    My orders
                  </Link>
                  <Link
                    to="/account"
                    onClick={() => setMobileOpen(false)}
                    className="mt-3 font-serif text-2xl text-espresso block"
                    data-testid="mobile-account-link"
                  >
                    My account
                  </Link>
                  {user.is_admin && (
                    <Link
                      to="/admin/dashboard"
                      onClick={() => setMobileOpen(false)}
                      className="mt-3 font-serif text-2xl text-gold-hover block"
                    >
                      Admin dashboard
                    </Link>
                  )}
                  <button
                    onClick={doLogout}
                    data-testid="mobile-logout"
                    className="mt-4 font-serif text-2xl text-espresso flex items-center gap-3"
                  >
                    <LogOut size={22} strokeWidth={1.5} /> Log out
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      <div className="site-nav-spacer" aria-hidden="true" />
    </>
  );
}
